/**
 * extensions/cornerstone/src/modules/suv/suvDefaults.js
 *
 * Global, mutable SUV defaults used when DICOM metadata is missing.
 *
 * Why this exists:
 *   Anonymized public datasets (TCIA PSMA-PET-CT-Lesions, autoPET subsets,
 *   many retrospective collections) often strip RadiopharmaceuticalInformation-
 *   Sequence. Without these tags the standard SUV formula returns nothing.
 *   This module provides per-tracer population-mean fallbacks, and exposes
 *   them in a single mutable object so a UI panel can adjust them at runtime
 *   for a given trial / cohort.
 *
 * Source numbers used as initial defaults:
 *   - LMU PSMA-PET-CT dataset (Jeblick et al. Sci Data 2026 v2):
 *       18F-PSMA: 246 MBq mean dose, 74 min mean uptake (369 studies)
 *       68Ga-PSMA: 214 MBq mean dose, 74 min mean uptake (228 studies)
 *
 * Conventions:
 *   - Dose in MBq (converted to Bq in computation)
 *   - Uptake time in minutes (converted to seconds in computation)
 *   - Patient weight in kg (DICOM PatientWeight convention)
 *   - Half-life in seconds (DICOM RadionuclideHalfLife convention)
 *
 * Usage:
 *   import { getSuvDefaults, setSuvDefaults } from '.../suvDefaults';
 *   const d = getSuvDefaults();                          // read
 *   setSuvDefaults({ defaultDoseMBq_18F: 250 });         // partial update
 */

const HALF_LIVES_SEC = {
  'F-18': 6586, // 109.77 min
  'Ga-68': 4080, // 68.00 min
  'Cu-64': 45720,
  'Zr-89': 282240,
};

/* The single mutable defaults object. Treat as a config blob — only mutate
 * via setSuvDefaults() so listeners can react in the future if needed. */
const defaults = {
  /* Toggle: when true, missing DICOM dose/weight is filled from this object;
   * when false, missing metadata produces null SUV (strict mode). */
  enableFallback: true,

  /* Per-tracer population means. Used when RadionuclideTotalDose absent. */
  defaultDoseMBq_18F: 246,
  defaultDoseMBq_68Ga: 214,
  defaultUptakeMinutes: 74,

  /* Used when PatientWeight (0010,1030) is missing. 70 kg is a clinical
   * default; trial SOPs may override. */
  defaultPatientWeightKg: 70,

  /* Auto-detect tracer when RadionuclideCodeSequence missing. Conservative
   * default is F-18 since that's the larger subgroup in your demo dataset.
   * If you switch to a 68Ga-only trial, change this in the UI/config. */
  defaultTracer: 'F-18',

  HALF_LIVES_SEC,
};

export function getSuvDefaults() {
  return { ...defaults };
}

export function setSuvDefaults(patch) {
  if (!patch || typeof patch !== 'object') return;
  Object.assign(defaults, patch);
}

/**
 * Map a tracer hint string (free-text from DICOM CodeMeaning, ROI label, etc.)
 * to a canonical tracer key. Falls back to defaults.defaultTracer.
 */
export function inferTracer(hintString) {
  if (!hintString) return defaults.defaultTracer;
  const t = String(hintString).toLowerCase();
  if (t.includes('68') || t.includes('gallium') || t.includes('ga-'))
    return 'Ga-68';
  if (t.includes('18') || t.includes('fluor') || t.includes('f-'))
    return 'F-18';
  return defaults.defaultTracer;
}

/**
 * Population-mean fallback for the decay-corrected dose at scan reference
 * time, in Bq. Used when RadionuclideTotalDose is missing.
 *
 *   doseAtScanTime = doseMBq_at_injection × 1e6 × exp(-ln2 × uptakeSec / halfLifeSec)
 */
export function getFallbackDecayCorrectedDoseBq(tracer) {
  const d = defaults;
  const meanDoseMBq =
    tracer === 'Ga-68' ? d.defaultDoseMBq_68Ga : d.defaultDoseMBq_18F;
  const uptakeSec = d.defaultUptakeMinutes * 60;
  const halfLifeSec = HALF_LIVES_SEC[tracer] || HALF_LIVES_SEC['F-18'];
  const decayFactor = Math.exp((-Math.log(2) * uptakeSec) / halfLifeSec);
  return meanDoseMBq * 1e6 * decayFactor;
}

export { HALF_LIVES_SEC };
